import React, { useEffect, useRef, useState } from "react"
import { useSession } from "../../../../../hooks/auth/useSession.hook"
import HistoricalTasksDataTable from "./HistoricalTasksDataTable"
import Card from "../../../../../components/atoms/Card.atom"
import HistoricalTasksControls from "./HistoricalTasksControls"
import { UNIFIEDDATE_VALUES } from "../../../../../consts/CompoundRangeDatePicker"
import {
  getMondayOfCurrentWeek,
  getSundayOfCurrentWeek,
} from "../../../../../utils/date.utils"
import {
  getLazyUserHistoricalPendingTasks,
  getLazyUserPendingTasksFollowers,
  getLazyUserPendingTasksProjects,
} from "../../../../../services/pendingTask.service"
import {
  DEFAULT_HISTORICAL_PENDING_TASKS_FILTER_CONFIG,
  DEFAULT_HISTORICAL_PENDING_TASKS_SORT_CONFIG,
  HISTORICAL_PENDING_TASK_HEADER_HEIGHT,
  HISTORICAL_PENDING_TASK_ROW_HEIGHT,
} from "../../../../../consts/modules/pendingTasks.consts"
import useToast from "../../../../../hooks/useToast.hook"

const HistoricalTasks = ({ setTodoTasksLength = () => {} }) => {
  // Table states
  const [data, setData] = useState([])
  const [teamMembers, setTeamMembers] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [lazyLoading, setLazyLoading] = useState(false)
  const [period, setPeriod] = useState(UNIFIEDDATE_VALUES.SINGLEWEEK)
  const [dateRange, setDateRange] = useState([
    getMondayOfCurrentWeek(),
    getSundayOfCurrentWeek(),
  ])
  // Lazy loading states
  const [sortConfig, setSortConfig] = useState(
    DEFAULT_HISTORICAL_PENDING_TASKS_SORT_CONFIG
  )
  const [filterConfig, setFilterConfig] = useState(
    DEFAULT_HISTORICAL_PENDING_TASKS_FILTER_CONFIG
  )

  // Collapse unfinish states
  const [lastFinishChangeTaskID, setLastFinishChangeTaskID] = useState(null)
  const [taskIsBeingHidden, setTaskIsBeingHidden] = useState(false)

  const [refresh, setRefresh] = useState(false)
  const [inlineLoading, setInlineLoading] = useState(false)
  // References
  const tableRef = useRef()
  const lastFirst = useRef(0)
  const lastTotalElements = useRef(0)
  const startScrollIndex = useRef(0)
  const endScrollIndex = useRef(0)
  const tableHeight = useRef(0)

  const { user } = useSession()
  const { setErrorMessage } = useToast()
  useEffect(() => {
    if (
      !taskIsBeingHidden
      // (taskIsBeingHidden && endScrollIndex.current < 50)
    ) {
      setData(
        generateEmptyData(
          Math.min(
            7,
            (tableHeight.current + HISTORICAL_PENDING_TASK_HEADER_HEIGHT) /
              HISTORICAL_PENDING_TASK_ROW_HEIGHT
          )
        )
      )
      lastFirst.current = 0
    }
    fetchAllData()
  }, [dateRange, sortConfig, filterConfig, refresh])

  useEffect(() => {
    tableHeight.current = calculateTableHeight(data.length || 5)
  }, [data.length])

  const triggerRefresh = () => setRefresh(!refresh)

  const setGlobalFilter = (value) => {
    setFilterConfig((prev) => ({
      ...prev,
      globalFilter: value,
    }))
  }

  const calculateTableHeight = (numberOfRows) => {
    return (
      numberOfRows * HISTORICAL_PENDING_TASK_ROW_HEIGHT +
      HISTORICAL_PENDING_TASK_HEADER_HEIGHT
    )
  }

  const generateEmptyData = (numberOfRows) => {
    return Array.from({ length: numberOfRows }).map(() => ({
      projectCode: null,
      clientCode: null,
      followerIds: [undefined],
    }))
  }

  const fetchAllData = async () => {
    setLoading(true)
    setLazyLoading(true)
    try {
      await Promise.all([
        gettingHistoricalTasks(),
        gettingTeamMembersData(),
        gettingProjectsData(),
      ])
    } catch (error) {
      console.error("Error fetching data:", error)
      setData([])
      setErrorMessage({
        title: "Error",
        message: "No se pudo cargar la información",
      })
    } finally {
      setLoading(false)
      setLazyLoading(false)
      setInlineLoading(false)
      setTaskIsBeingHidden(false)
    }
  }
  const gettingHistoricalTasks = async (first = 0, last = 75) => {
    first = lastFirst.current || first
    last = first + 75
    const response = await getLazyUserHistoricalPendingTasks(
      filterConfig,
      sortConfig,
      dateRange[0],
      dateRange[1],
      first,
      last,
      user.id
    )
    if (!response.success) {
      throw new Error(response.message)
    }
    if (response.result.length === 0) {
      setData([])
      lastTotalElements.current = 0
      return
    }
    lastTotalElements.current = response?.result?.[0]?.totalElements ?? 5
    let wholeData = [...data]
    if (data.every((task) => task.projectCode === null)) {
      wholeData = generateEmptyData(lastTotalElements.current)
    }
    Array.prototype.splice.apply(wholeData, [
      ...[first, response.result.length],
      ...response.result,
    ])
    wholeData = wholeData.slice(0, lastTotalElements.current)
    setData(wholeData)
    setTodoTasksLength((prev) => response?.result?.[0]?.todoTasksCount || prev)
  }

  const gettingTeamMembersData = async () => {
    const response = await getLazyUserPendingTasksFollowers(
      dateRange[0],
      dateRange[1]
    )
    if (response.success) {
      setTeamMembers(response.result)
    }
  }

  const gettingProjectsData = async () => {
    const response = await getLazyUserPendingTasksProjects(
      dateRange[0],
      dateRange[1]
    )
    if (response.success) {
      setProjects(response.result)
    }
  }

  const searchResultTemplate = () => {
    return (
      <div
        className={`flex ${loading ? "align-items-center" : "align-items-end"} font-bold text-md mb-3 ${loading ? "opacity-50" : ""} gap-1`}
      >
        {loading ?
          <i className="pi pi-spin pi-spinner text-md m-0 line-height-1"></i>
        : `${data.length}`}
        <span>resultados de búsqueda:</span>
      </div>
    )
  }

  const onLazyLoad = async (event) => {
    const first = event?.first
    const last = event?.last + 25
    const loadedTasks = data.slice(0, lastFirst.current)
    const hasMissingPreviousData = loadedTasks.some(
      (task) => task.projectCode === null
    )
    // console.log("onLazyLoad", event, lastFirst.current)
    if (first > last) {
      setLazyLoading(false)
      return
    }
    if (
      first >= lastFirst.current ||
      (first < lastFirst.current && hasMissingPreviousData)
    ) {
      if (loading) return setLazyLoading(false)

      setLazyLoading(true)
      if (first > last) {
        setLazyLoading(false)
        return
      }
      try {
        const response = await getLazyUserHistoricalPendingTasks(
          filterConfig,
          sortConfig,
          dateRange[0],
          dateRange[1],
          first,
          last,
          user.id
        )

        if (response.success) {
          lastTotalElements.current = response?.result?.[0]?.totalElements || 5
          const copyData = [...data]
          Array.prototype.splice
            .apply(copyData, [
              first,
              response.result.length,
              ...response.result,
            ])
            .slice(first, lastTotalElements.current)
          setData(copyData)
        }
      } catch (error) {
        console.error("Error al cargar tareas históricas:", error)
      } finally {
        setLazyLoading(false)
      }
      lastFirst.current = first
    } else {
      setData((prev) => {
        const newData = [...prev]
        newData
          .splice(0, lastFirst.current, ...loadedTasks)
          .slice(0, lastTotalElements.current)
        return newData
      })
      setLazyLoading(false)
    }
    setTaskIsBeingHidden(false)
  }

  const onScrollIndexChange = ({ first }) => {
    const tbodyEl = tableRef.current?.getTable()?.querySelector("tbody") ?? null

    if (tbodyEl === null) {
      return
    }
    tbodyEl.style.transform = `translate3d(0px, ${startScrollIndex.current * HISTORICAL_PENDING_TASK_ROW_HEIGHT}px, 0px)`
    if (endScrollIndex.current !== startScrollIndex.current) {
      endScrollIndex.current = first
      return
    }

    endScrollIndex.current = first

    const observer = new MutationObserver(() => {
      observer.disconnect()
      startScrollIndex.current = endScrollIndex.current
      tbodyEl.style.transform = `translate3d(0px, ${endScrollIndex.current * HISTORICAL_PENDING_TASK_ROW_HEIGHT}px, 0px)`
    })
    observer.observe(tbodyEl, { childList: true })
  }

  const hasFilters = () => {
    if (filterConfig.globalFilter !== "") return true
    if (filterConfig.projectCode && filterConfig.projectCode.length > 0)
      return true
    if (filterConfig.projectName && filterConfig.projectName.length > 0)
      return true
    if (filterConfig.clientCode !== null) return true
    if (filterConfig.followerIds && filterConfig.followerIds.length > 0)
      return true
    if (filterConfig.taskName !== null) return true
    return false
  }

  const resetControls = () => {
    setPeriod(UNIFIEDDATE_VALUES.SINGLEWEEK)
    setDateRange([getMondayOfCurrentWeek(), getSundayOfCurrentWeek()])
    setGlobalFilter("")
    setSortConfig(DEFAULT_HISTORICAL_PENDING_TASKS_SORT_CONFIG)
    setFilterConfig(DEFAULT_HISTORICAL_PENDING_TASKS_FILTER_CONFIG)
    lastFirst.current = 0
    tableRef.current?.reset()
  }

  return (
    <Card className="historical-pending-task-table shadow-2">
      <HistoricalTasksControls
        period={period}
        setPeriod={setPeriod}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onReset={resetControls}
        globalFilter={filterConfig.globalFilter}
        setGlobalFilter={setGlobalFilter}
        searchResultTemplate={searchResultTemplate}
        isLoading={loading}
      />
      {filterConfig.globalFilter !== "" && searchResultTemplate()}
      <HistoricalTasksDataTable
        ref={tableRef}
        historicalTasks={data}
        lazyLoading={lazyLoading}
        setLazyLoading={setLazyLoading}
        teamMembers={teamMembers}
        projects={projects}
        sortConfig={sortConfig}
        loadingProjects={projects.length === 0 && loading}
        loadingState={loading}
        setSortConfig={setSortConfig}
        setFilterConfig={setFilterConfig}
        triggerRefresh={triggerRefresh}
        inlineLoading={inlineLoading}
        setInlineLoading={setInlineLoading}
        height={tableHeight.current}
        lastFinishChangeTaskID={lastFinishChangeTaskID}
        setLastFinishChangeTaskID={setLastFinishChangeTaskID}
        taskIsBeingHidden={taskIsBeingHidden}
        setTaskIsBeingHidden={setTaskIsBeingHidden}
        emptyCustomMessage={
          <CustomEmptyMessage
            emptyMessageTitle={"Sin información"}
            emptyMessageContent={
              hasFilters() ?
                "Intenta aplicar otros filtros"
              : "Cuando finalices tareas, se mostrarán aquí al día siguiente."
            }
            height={228}
          />
        }
        virtualScrollerOptions={{
          className: "w-full",
          itemSize: HISTORICAL_PENDING_TASK_ROW_HEIGHT,
          numToleratedItems: 20,
          lazy: true,
          onLazyLoad,
          onScrollIndexChange,
          items: data,
        }}
      />
    </Card>
  )
}

const CustomEmptyMessage = ({
  emptyMessageTitle = "Sin información",
  emptyMessageContent = "Intenta aplicar otros filtros",
  width = "auto",
  height = "auto",
}) => {
  return (
    <div
      className="empty-data-historical-pending-tasks-message text-center flex flex-column justify-content-center"
      style={{ height: height, width: width }}
    >
      <i className="pi pi-folder-open"></i>
      <h3 className="title font-cairo">{emptyMessageTitle}</h3>
      <p className="message">{emptyMessageContent}</p>
    </div>
  )
}

export default HistoricalTasks
